/**
 * Normalizes Twilio Voice SDK `Device`/`Call` errors into sanitized,
 * user-friendly notification content and provides a small de-dupe window so
 * bursts of identical errors do not spam the toast system.
 *
 * The raw Twilio error (which may embed tokens, SIDs, stack traces and the
 * `originalError`) is never surfaced to the user. Only the mapped, sanitized
 * strings below are shown; full diagnostics are logged separately via the
 * store's controlled `console.error` call.
 */

/** Shape of the subset of a Twilio error we rely on. Kept loose because the
 * SDK emits both structured `TwilioError` instances and plain `Error`s. */
export interface TwilioLikeError {
    code?: number;
    message?: string;
    name?: string;
}

/** Sanitized content ready for the toast + inline dialer panel. */
export interface NormalizedTwilioError {
    /** Toast title. */
    title: string;
    /** Toast body message. */
    message: string;
    /** Readable string for the inline dialer error panel. */
    inlineMessage: string;
    /** Stable key used to throttle duplicate toasts. */
    dedupeKey: string;
}

const DEFAULT_TITLE = 'Call error';

/**
 * Friendly, sanitized messages keyed by Twilio error code. Only concise,
 * user-appropriate copy lives here; never SID/token/stack details.
 */
const FRIENDLY_MESSAGES: Record<number, string> = {
    // Microphone access denied/unavailable. Reactive fallback only — the
    // proactive preflight (#1917) remains the primary guard for this case.
    31208: 'Microphone access was denied or is unavailable. Please allow microphone access and try again.',
    31401: 'Microphone access was denied or is unavailable. Please allow microphone access and try again.',
    31402: 'No microphone could be found. Please connect a microphone and try again.',

    // Signaling / connectivity failures.
    31001: 'The calling service is temporarily unavailable. Please try again shortly.',
    31002: 'The call could not be connected. Please try again.',
    31003: 'The call timed out while connecting. Please check your connection and try again.',
    31005: 'A connection error interrupted the call. Please try again.',
    31009: 'A network transport error occurred. Please check your connection and try again.',
    53000: 'A network connection problem interrupted the call. Please check your connection and try again.',
    53001: 'The network connection was lost. Please check your connection and try again.',
    53405: 'The call could not establish audio. Please check your connection and try again.',
};

/**
 * Returns a friendly message for a given Twilio error code, applying range
 * based fallbacks before the final generic fallback.
 */
function messageForCode(code: number | undefined): string {
    if (code === undefined) return 'An unexpected calling error occurred. Please try again.';

    if (FRIENDLY_MESSAGES[code]) return FRIENDLY_MESSAGES[code];

    // Authentication / token failures (312xx) other than the mic case (31208).
    if (code >= 31200 && code < 31300 && code !== 31208)
        return 'Your calling session could not be authorized. Please refresh and try again.';

    // Generic signaling family (53xxx).
    if (code >= 53000 && code < 54000)
        return 'A network connection problem interrupted the call. Please check your connection and try again.';

    return 'An unexpected calling error occurred. Please try again.';
}

/**
 * Converts an arbitrary Twilio Voice error into sanitized notification content.
 */
export function normalizeTwilioError(error: TwilioLikeError | undefined): NormalizedTwilioError {
    const code = typeof error?.code === 'number' ? error.code : undefined;
    const message = messageForCode(code);

    return {
        title: DEFAULT_TITLE,
        message,
        inlineMessage: message,
        dedupeKey: code !== undefined ? `code:${code}` : `msg:${message}`,
    };
}

/**
 * Module-level de-dupe gate. Returns `true` when a toast for the given key may
 * be shown, and records the timestamp; returns `false` while an identical key
 * is still within the throttle window.
 */
const lastShownByKey = new Map<string, number>();
const DEDUPE_WINDOW_MS = 5000;

export function shouldNotify(dedupeKey: string, now: number = Date.now()): boolean {
    const last = lastShownByKey.get(dedupeKey);
    if (last !== undefined && now - last < DEDUPE_WINDOW_MS) return false;
    lastShownByKey.set(dedupeKey, now);
    return true;
}

/** Test/utility helper to reset the de-dupe state. */
export function resetTwilioErrorDedupe(): void {
    lastShownByKey.clear();
}
